import React, { ReactNode, useEffect, useState, useRef } from "react";
import { Badge, Tooltip, Button, Modal } from "antd";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { Calendar01Icon } from "@hugeicons/core-free-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import Navbar from "../Navbar/Navbar";
import { useAssessmentTheme } from "../../hooks/useAssessmentTheme";
import { ChatbotToggle } from "../AIChatbot";
import logo from "../../assets/P3M_logo.svg";

import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { useMaxWidth } from "../../hooks/useMaxWidth";
import { RootState } from "../../redux/store";
import {
    minimalToast,
    NotificationCategory,
    NotificationPriority,
    NotificationSeverity,
} from "../../utils/toast";
import { logout } from "../../redux/auth/auth.slice";
import { buildLoginPath } from "../../utils/returnPath";
import {
    fetchUserNotifications,
    fetchNotificationSummary,
    markNotificationsRead,
} from "../../redux/notifications/notification.slice";
import {
    notificationWindowFrom,
    NOTIFICATION_DRAWER_DAYS,
} from "../../utils/notificationUtils";
import { SidebarNavSkeleton } from "../Shimmers/AppShellSkeletons";

import {
    toggleCollapsed,
    closeMobileSidebar,
} from "../../redux/sidebar/sidebar.slice";

import { SIDEBAR_DRAWER_MAX_WIDTH } from "../../constants/layout";

import "./sidebar.scss";
import TruncateWithTooltip from "./TruncateWithTooltip";
import { AnimatedIcon } from "./icons/AnimatedIcon";
import { ICON_ANIMATION_PRESETS } from "./icons/presets";
import {
    HomeIcon,
    LogoutIcon,
    BugIcon,
    ChartBarIcon,
    FileDescriptionIcon,
    LayoutDashboardIcon,
    LayoutSidebarRightCollapseIcon,
    LayoutSidebarRightIcon,
    QuestionMark,
    ShoppingCartIcon,
    UsersGroupIcon,
    VinylIcon,
    ShieldCheck,
    CheckedIcon,
    MagnifierIcon,
    PenIcon,
    StackIcon,
    CameraIcon,
    MessageCircleIcon,
    FilledBellIcon,
    LibraryIcon,
    UsersIcon,
    GaugeIcon,
    BrainCircuitIcon,
    ClockIcon,
    SparklesIcon,
    CopyIcon,
    ChartLineIcon,
    HashtagIcon,
    UnorderedListIcon,
} from "./icons/itshover";

// Helper component for HugeIcons in sidebar. Tracks its own hover state so a
// handful of icons (see icons/presets.ts) can animate individual strokes -
// a door easing open, an arrow sliding out - instead of just scaling the
// whole glyph. Icons without a bespoke preset render exactly as before.
const Icon: React.FC<{ icon: any; size?: number }> = ({ icon, size = 20 }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <span
            className="sidebar-icon-hover"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <AnimatedIcon
                icon={icon}
                size={size}
                hovered={hovered}
                preset={ICON_ANIMATION_PRESETS.get(icon)}
            />
        </span>
    );
};

type SidebarProps = {
    children?: ReactNode;
};

// Stagger children animation
const staggerContainer = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.04,
            delayChildren: 0.1,
        },
    },
};

const itemFadeIn = {
    hidden: { opacity: 0, x: -12 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.3, ease: "easeOut" },
    },
};

// Submenu parent key -> the route bases of its children. Keys are stable across
// roles, so this lets us auto-expand the group containing the active route
// without depending on the role-built menu sections (which are computed after
// this component's early returns). Expanding a key whose group isn't rendered
// for the current role is harmless.
const SUBMENU_ROUTES: Record<string, string[]> = {
    "audits-submenu": ["/audits", "/ai-audits", "/one-time-audits"],
    "assessments-submenu": [
        "/assessments",
        "/question-bank",
        "/question-banks",
        "/question-papers",
    ],
    "evaluation-submenu": ["/evaluations", "/evaluation-students"],
};


const Sidebar: React.FC<SidebarProps> = ({ children }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const sidebarRef = useRef<HTMLDivElement>(null);
    const theme = useAssessmentTheme();

    const isSidebarDrawer = useMaxWidth(SIDEBAR_DRAWER_MAX_WIDTH);

    const { collapsed, mobileSidebarVisible } = useAppSelector(
        (state: RootState) => state.sidebar
    );

    const user = useAppSelector((state: RootState) => state.auth.user);
    const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated);
    const accessToken = useAppSelector((state: RootState) => state.auth.access_token);
    // Give up on the skeleton after a few seconds so a failed user-load (e.g. a
    // backend/network error) can never leave the nav stuck loading.
    const [hydrationTimedOut, setHydrationTimedOut] = useState(false);
    useEffect(() => {
        if (!accessToken || user) return;
        const t = setTimeout(() => setHydrationTimedOut(true), 6000);
        return () => clearTimeout(t);
    }, [accessToken, user]);
    // A session token exists but the user details haven't loaded yet: show
    // skeleton nav rows instead of a half-built (role/feature-dependent) menu.
    const shellHydrating = !!accessToken && !user && !hydrationTimedOut;

    const departments = useAppSelector(
        (state: RootState) =>
            state.organization.selectedOrganization?.departments
    );
    const departmentTypes = departments?.[0]?.type ?? "Department";
    const selectedOrganization = useAppSelector(
        (state: RootState) => state.organization.selectedOrganization
    );

    const admin = useAppSelector((state: RootState) => state.auth.adminMode);
    const owner = useAppSelector((state: RootState) => state.auth.ownerMode);
    const superAdmin = useAppSelector(
        (state: RootState) => state.auth.superAdminMode
    );
    const p3mSuperAdmin = useAppSelector(
        (state: RootState) => state.auth.p3mSuperAdminMode
    );
    const auditor = useAppSelector((state: RootState) =>
        state.auth.user?.role?.includes("Auditor")
    );
    const isEvaluator = useAppSelector((state: RootState) =>
        state.auth.user?.role?.includes("Evaluator")
    );

    const hasFeature = (featureName: keyof NonNullable<typeof selectedOrganization>['features']) => {
        return selectedOrganization?.features?.[featureName] ?? false;
    };

    const hasCustomizationFlag = (key: string) =>
        (selectedOrganization as any)?.customization?.[key] === true;

    // Workshop certificates exist only in an org whose switch is on (P3M's own).
    // There, P3M staff manage them and the org's Super Admin, Owner or Admin
    // see them read-only. The item is hidden everywhere else, for staff too.
    const workshopCertificatesOn = hasCustomizationFlag("workshopCertificates");
    const workshopCertificatesItems = (show: boolean) =>
        show
            ? [{ key: "/workshop-certificates", icon: <ShieldCheck size={20} />, label: "Certificates" }]
            : [];

    // Notifications (drawer list is loaded separately in Navbar/Sidebar window)
    const notificationSummary = useAppSelector(
        (state: RootState) => state.notifications.summary
    );
    const taskCount = notificationSummary.taskUnread;
    const auditCount = notificationSummary.auditUnread;
    const showTaskBadge = taskCount > 0;
    const showAuditBadge = auditCount > 0;
    // Submenu expand state - must be declared before any early return so the
    // hook count stays stable across route changes (React "rendered more hooks"
    // error otherwise fires when toggling to/from full-bleed or anonymous routes).
    const [expandedSubmenus, setExpandedSubmenus] = useState<string[]>([]);

    // Auto-expand the submenu that owns the current route (on load and on every
    // navigation), so deep-links land with the active group open. Merges into
    // the existing set so groups the user opened manually stay open.
    useEffect(() => {
        const path = location.pathname;
        const matchesBase = (base: string) =>
            path === base || path.startsWith(base + "/");
        const parentKey = Object.keys(SUBMENU_ROUTES).find((key) =>
            SUBMENU_ROUTES[key].some(matchesBase)
        );
        if (!parentKey) return;
        setExpandedSubmenus((prev) =>
            prev.includes(parentKey) ? prev : [...prev, parentKey]
        );
    }, [location.pathname]);

    useEffect(() => {
        if (user?._id) {
            dispatch(
                fetchUserNotifications({
                    userId: user._id,
                    from: notificationWindowFrom(NOTIFICATION_DRAWER_DAYS),
                })
            );
        }
    }, [dispatch, user?._id]);

    const selectedOrgId = selectedOrganization?._id;

    useEffect(() => {
        if (user?._id) {
            dispatch(
                fetchNotificationSummary({
                    userId: user._id,
                    organization: selectedOrgId,
                })
            );
        }
    }, [dispatch, user?._id, selectedOrgId]);

    const shouldHideSidebarAndNavbar = React.useMemo(() => {
        const isOneTimeAuditsRoute = location.pathname.includes('/one-time-audits');
        const searchParams = new URLSearchParams(location.search);
        const hasToken = searchParams.has('token');
        return isOneTimeAuditsRoute && hasToken && !isAuthenticated;
    }, [location.pathname, location.search, isAuthenticated]);

    /** Template Marketplace is a full-bleed workspace - renders without the main sidebar/navbar. */
    const isFullBleedRoute = React.useMemo(
        () => location.pathname === '/template-marketplace',
        [location.pathname]
    );

    if (shouldHideSidebarAndNavbar) {
        return <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>{children}</div>;
    }

    if (isFullBleedRoute) {
        return <div style={{ minHeight: '100vh', background: '#ffffff' }}>{children}</div>;
    }

    // Evaluator items
    const getEvaluatorItems = () => {
        const items: any[] = [];
        if (user?.adminFeaturesAllowed?.includes("NoticeBoard")) {
            items.push({
                key: "/notice",
                icon: <FilledBellIcon size={20} />,
                label: "Admin Notice Board",
            });
        }
        if (user?.adminFeaturesAllowed?.includes("ManageAuditee")) {
            items.push({
                key: "/all-employees",
                icon: <UsersIcon size={20} />,
                label: "Manage Auditee",
            });
        }
        if (user?.adminFeaturesAllowed?.includes("TaskAndGoals")) {
            items.push({
                key: "/task-and-goals",
                icon: <UnorderedListIcon size={20} />,
                label: "Admin Task And Goals",
            });
        }
        return items;
    };

    // ── Build menu sections (grouped) ──
    type MenuItem = {
        key: string;
        icon: React.ReactNode;
        label: string;
        badge?: number;
        betaTag?: boolean;
        children?: MenuItem[];
    };

    type MenuSection = {
        title: string;
        items: MenuItem[];
    };

    const buildMenuSections = (): MenuSection[] => {
        if (admin) {
            return [
                {
                    title: "Main",
                    items: [
                        { key: "/", icon: <HomeIcon size={20} />, label: "Home" },
                        { key: "/admin-dashboard", icon: <LayoutDashboardIcon size={20} />, label: "Admin Dashboard" },
                        { key: "/department-dashboard", icon: <GaugeIcon size={20} />, label: `${departmentTypes} Dashboard` },
                    ],
                },
                {
                    title: "Audit & Assessment",
                    items: [
                        {
                            key: "audits-submenu",
                            icon: <MagnifierIcon size={20} />,
                            label: "Audits",
                            badge: showAuditBadge ? auditCount : 0,
                            children: [
                                { key: "/audits", icon: <MagnifierIcon size={20} />, label: "Manual Audits", badge: showAuditBadge ? auditCount : 0 },
                                { key: "/ai-audits", icon: <BrainCircuitIcon size={20} />, label: "AI Audits", betaTag: true },
                                { key: "/one-time-audits", icon: <ClockIcon size={20} />, label: "One-Time Audits" },
                            ],
                        },
                        {
                            key: "assessments-submenu",
                            icon: <PenIcon size={20} />,
                            label: "Assessments",
                            children: [
                                { key: "/assessments", icon: <FileDescriptionIcon size={20} />, label: "All Assessments" },
                                { key: "/assessments/create", icon: <SparklesIcon size={20} />, label: "Create Assessment" },
                                { key: "/question-bank", icon: <QuestionMark size={20} />, label: "Questions" },
                                { key: "/question-banks/manage", icon: <StackIcon size={20} />, label: "Question Banks" },
                                { key: "/question-papers", icon: <CopyIcon size={20} />, label: "Question Papers" },
                            ],
                        },
                        {
                            key: "evaluation-submenu",
                            icon: <ChartBarIcon size={20} />,
                            label: "Evaluation",
                            children: [
                                { key: "/evaluations", icon: <ChartLineIcon size={20} />, label: "Evaluations" },
                                { key: "/evaluation-students", icon: <UsersGroupIcon size={20} />, label: "Schools & Students" },
                            ],
                        },
                        {
                            key: "/template-marketplace",
                            icon: <LibraryIcon size={20} />,
                            label: "Template Library",
                        },
                        ...(hasFeature('imageVerification') ? [{
                            key: "/image-verification",
                            icon: <CameraIcon size={20} />,
                            label: "Image Verification",
                        }] : []),
                        ...workshopCertificatesItems(workshopCertificatesOn),
                    ],
                },
                {
                    title: "Management",
                    items: [
                        { key: "/task-and-goals", icon: <CheckedIcon size={20} />, label: "Task Manager", badge: showTaskBadge ? taskCount : 0 },
                        { key: "/all-employees", icon: <UsersIcon size={20} />, label: "Manage Auditee" },
                        { key: "/notice", icon: <FilledBellIcon size={20} />, label: "Notice Board" },
                    ],
                },
                {
                    title: "Community",
                    items: [
                        { key: "/socials", icon: <MessageCircleIcon size={20} />, label: "Socials" },
                        { key: "/groups", icon: <HashtagIcon size={20} />, label: "All Groups" },
                        { key: "/tickets", icon: <BugIcon size={20} />, label: "Report Issue" },
                    ],
                },
            ];
        } else if (owner) {
            return [
                {
                    title: "Main",
                    items: [
                        { key: "/", icon: <HomeIcon size={20} />, label: "Home" },
                        { key: "/owner-dashboard", icon: <LayoutDashboardIcon size={20} />, label: "Owner Dashboard" },
                        { key: "/all-employees", icon: <UsersIcon size={20} />, label: "Manage Auditee" },
                    ],
                },
                {
                    title: "Audit & Assessment",
                    items: [
                        { key: "/template-marketplace", icon: <LibraryIcon size={20} />, label: "Template Library" },
                        ...workshopCertificatesItems(workshopCertificatesOn),
                    ],
                },
                {
                    title: "Community",
                    items: [
                        { key: "/socials", icon: <MessageCircleIcon size={20} />, label: "Socials" },
                        { key: "/notice", icon: <FilledBellIcon size={20} />, label: "Notice Board" },
                        { key: "/tickets", icon: <BugIcon size={20} />, label: "Report Issue" },
                    ],
                },
            ];
        } else if (superAdmin || auditor || p3mSuperAdmin) {
            return [
                {
                    title: "Main",
                    items: [
                        { key: "/", icon: <HomeIcon size={20} />, label: "Home" },
                        ...(admin || superAdmin || p3mSuperAdmin
                            ? [{ key: "/anecdotal-records", icon: <VinylIcon size={20} />, label: "Anecdotal Records" }]
                            : []),
                        { key: "/task-and-goals", icon: <CheckedIcon size={20} />, label: "Task Manager", badge: showTaskBadge ? taskCount : 0 },
                        { key: "/admin-dashboard", icon: <LayoutDashboardIcon size={20} />, label: "Admin Dashboard" },
                        { key: "/department-dashboard", icon: <GaugeIcon size={20} />, label: `${departmentTypes} Dashboard` },
                    ],
                },
                {
                    title: "Audit & Assessment",
                    items: [
                        {
                            key: "audits-submenu",
                            icon: <MagnifierIcon size={20} />,
                            label: "Audits",
                            badge: showAuditBadge ? auditCount : 0,
                            children: [
                                { key: "/audits", icon: <MagnifierIcon size={20} />, label: "Manual Audits", badge: showAuditBadge ? auditCount : 0 },
                                { key: "/ai-audits", icon: <BrainCircuitIcon size={20} />, label: "AI Audits", betaTag: true },
                                { key: "/one-time-audits", icon: <ClockIcon size={20} />, label: "One-Time Audits" },
                            ],
                        },
                        {
                            key: "assessments-submenu",
                            icon: <PenIcon size={20} />,
                            label: "Assessments",
                            children: [
                                { key: "/assessments", icon: <FileDescriptionIcon size={20} />, label: "All Assessments" },
                                { key: "/assessments/create", icon: <SparklesIcon size={20} />, label: "Create Assessment" },
                                { key: "/question-bank", icon: <QuestionMark size={20} />, label: "Questions" },
                                { key: "/question-banks/manage", icon: <StackIcon size={20} />, label: "Question Banks" },
                                { key: "/question-papers", icon: <CopyIcon size={20} />, label: "Question Papers" },
                            ],
                        },
                        {
                            key: "evaluation-submenu",
                            icon: <ChartBarIcon size={20} />,
                            label: "Evaluation",
                            children: [
                                { key: "/evaluations", icon: <ChartLineIcon size={20} />, label: "Evaluations" },
                                { key: "/evaluation-students", icon: <UsersGroupIcon size={20} />, label: "Schools & Students" },
                            ],
                        },
                        {
                            key: "/template-marketplace",
                            icon: <LibraryIcon size={20} />,
                            label: "Template Library",
                        },
                        ...(hasFeature('imageVerification') ? [{
                            key: "/image-verification",
                            icon: <CameraIcon size={20} />,
                            label: "Image Verification",
                        }] : []),
                        ...workshopCertificatesItems(workshopCertificatesOn && (p3mSuperAdmin || superAdmin)),
                    ],
                },
                {
                    title: "Management",
                    items: [
                        { key: "/all-employees", icon: <UsersIcon size={20} />, label: "Manage Auditee" },
                        { key: "/notice", icon: <FilledBellIcon size={20} />, label: "Notice Board" },
                    ],
                },
                {
                    title: "Community",
                    items: [
                        { key: "/socials", icon: <MessageCircleIcon size={20} />, label: "Socials" },
                        { key: "/groups", icon: <HashtagIcon size={20} />, label: "All Groups" },
                        { key: "/tickets", icon: <BugIcon size={20} />, label: "Report Issue" },
                    ],
                },
                ...(p3mSuperAdmin
                    ? [{
                        title: "Super Admin",
                        items: [
                            { key: "/register-organization-super-admin", icon: <ShieldCheck size={20} />, label: "Register Org SuperAdmin" },
                            { key: "/billing", icon: <ShoppingCartIcon size={20} />, label: "Billing Dashboard" },
                            { key: "/d2c/orders", icon: <StackIcon size={20} />, label: "Direct Orders" },
                            { key: "/d2c/attention", icon: <FilledBellIcon size={20} />, label: "Direct Attention" },
                        ],
                    }]
                    : []),
            ];
        } else {
            // Regular user
            return [
                {
                    title: "Main",
                    items: [
                        { key: "/", icon: <HomeIcon size={20} />, label: "Home" },
                        { key: "/calendar", icon: <Icon icon={Calendar01Icon} />, label: "Calendar" },
                        ...(admin || superAdmin || p3mSuperAdmin || user?.adminFeaturesAllowed?.includes("AnecdotalRecords")
                            ? [{ key: "/anecdotal-records", icon: <VinylIcon size={20} />, label: "Anecdotal Records" }]
                            : []),
                    ],
                },
                {
                    title: "Audit",
                    items: [
                        {
                            key: "audits-submenu",
                            icon: <MagnifierIcon size={20} />,
                            label: "Audits",
                            badge: showAuditBadge ? auditCount : 0,
                            children: [
                                { key: "/audits", icon: <MagnifierIcon size={20} />, label: "Manual Audits", badge: showAuditBadge ? auditCount : 0 },
                                { key: "/ai-audits", icon: <BrainCircuitIcon size={20} />, label: "AI Audits", betaTag: true },
                                { key: "/one-time-audits", icon: <ClockIcon size={20} />, label: "One-Time Audits" },
                            ],
                        },
                        { key: "/template-marketplace", icon: <LibraryIcon size={20} />, label: "Template Library" },
                    ],
                },
                {
                    title: "Personal",
                    items: [
                        {
                            key: `/dashboard/${user?._id}`,
                            icon: <ChartBarIcon size={20} />,
                            label: `${user?.name ? user.name.charAt(0).toUpperCase() + user.name.slice(1) : "User"}'s Dashboard`,
                        },
                        { key: "/user-task-and-goals", icon: <CheckedIcon size={20} />, label: "Task Manager", badge: showTaskBadge ? taskCount : 0 },
                    ],
                },
                {
                    title: "Community",
                    items: [
                        { key: "/socials", icon: <MessageCircleIcon size={20} />, label: "Socials" },
                        { key: "/tickets", icon: <BugIcon size={20} />, label: "Report Issue" },
                        ...(isEvaluator ? getEvaluatorItems() : []),
                    ],
                },
            ];
        }
    };

    const menuSections = buildMenuSections();

    const toggleSubmenu = (key: string) => {
        setExpandedSubmenus((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    // ── Navigation handler ──
    const handleNavClick = (key: string) => {
        if (key === "/audits") {
            if (user?._id && selectedOrgId) {
                dispatch(
                    markNotificationsRead({
                        userId: user._id,
                        organization: selectedOrgId,
                        category: "audit",
                    })
                );
            }
            navigate("/audits");
        } else if (key === "/task-and-goals" || key === "/user-task-and-goals") {
            if (user?._id && selectedOrgId) {
                dispatch(
                    markNotificationsRead({
                        userId: user._id,
                        organization: selectedOrgId,
                        category: "reminder",
                    })
                );
            }
            navigate(key);
        } else {
            navigate(key);
        }

        if (isSidebarDrawer) {
            dispatch(closeMobileSidebar());
        }
    };

    const handleLogout = () => {
        Modal.confirm({
            title: "Log out?",
            content: "You'll need to sign in again to continue.",
            okText: "Log out",
            okButtonProps: { danger: true },
            cancelText: "Cancel",
            centered: true,
            onOk: () => {
                dispatch(logout());
                // Carry the current page so signing back in returns here.
                navigate(buildLoginPath(location, { owner }));
                minimalToast({
                    dispatch,
                    title: "Success",
                    message: "User has been logged out Successfully!!",
                    type: NotificationSeverity.SUCCESS,
                    category: NotificationCategory.ALERT,
                    priority: NotificationPriority.HIGH,
                });
            },
        });
    };

    const handleCollapse = () => {
        dispatch(toggleCollapsed());
    };

    // Prefix-aware active match: a list route stays highlighted on its detail
    // routes (e.g. /audits also matches /audits/2/:id). Home ("/") is exact-only
    // so it doesn't match every path; the trailing slash stops /audits from
    // matching /ai-audits.
    const isRouteActive = (key: string) => {
        if (!key || key.startsWith("/") === false) return false;
        if (key === "/") return location.pathname === "/";
        return (
            location.pathname === key ||
            location.pathname.startsWith(key + "/")
        );
    };
    const isActive = (key: string) => isRouteActive(key);
    const isSubmenuActive = (item: MenuItem) =>
        item.children?.some((child) => isRouteActive(child.key)) ?? false;

    // ── Render a single nav item ──
    const renderNavItem = (item: MenuItem, _index: number) => {
        const active = isActive(item.key);

        // For collapsed desktop, show icon-only with tooltip
        if (!isSidebarDrawer && collapsed) {
            if (item.children) {
                const subActive = isSubmenuActive(item);
                return (
                    <Tooltip
                        key={item.key}
                        title={item.label}
                        placement="right"
                        mouseEnterDelay={0}
                        classNames={{ root: "sidebar-tooltip" }}
                    >
                        <motion.button
                            type="button"
                            aria-label={item.label}
                            aria-current={subActive ? "page" : undefined}
                            className={`nav-item nav-item--icon-only ${subActive ? "nav-item--active" : ""}`}
                            whileHover={{ scale: 1.08 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleNavClick(item.children![0].key)}
                        >
                            <div className="nav-item__icon">
                                {item.icon}
                                {item.badge && item.badge > 0 ? (
                                    <span className="nav-item__dot" />
                                ) : null}
                            </div>
                        </motion.button>
                    </Tooltip>
                );
            }

            return (
                <Tooltip
                    key={item.key}
                    title={item.label}
                    placement="right"
                    mouseEnterDelay={0}
                    classNames={{ root: "sidebar-tooltip" }}
                >
                    <motion.button
                        type="button"
                        aria-label={item.label}
                        aria-current={active ? "page" : undefined}
                        className={`nav-item nav-item--icon-only ${active ? "nav-item--active" : ""}`}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleNavClick(item.key)}
                    >
                        <div className="nav-item__icon">
                            {item.icon}
                            {item.badge && item.badge > 0 ? (
                                <span className="nav-item__dot" />
                            ) : null}
                        </div>
                    </motion.button>
                </Tooltip>
            );
        }

        // Submenu items (expanded mode)
        if (item.children) {
            const isOpen = expandedSubmenus.includes(item.key);
            const subActive = isSubmenuActive(item);

            return (
                <div key={item.key} className="nav-submenu">
                    <motion.button
                        type="button"
                        aria-expanded={isOpen}
                        aria-current={subActive ? "page" : undefined}
                        className={`nav-item ${subActive ? "nav-item--active" : ""}`}
                        variants={itemFadeIn}
                        onClick={() => toggleSubmenu(item.key)}
                    >
                        <div className="nav-item__icon">{item.icon}</div>
                        <span className="nav-item__label">
                            <TruncateWithTooltip maxWidth={110}>{item.label}</TruncateWithTooltip>
                        </span>
                        {item.badge && item.badge > 0 ? (
                            <Badge count={item.badge} size="small" className="nav-item__badge" />
                        ) : null}
                        <motion.span
                            className="nav-item__arrow"
                            animate={{ rotate: isOpen ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <RightOutlined />
                        </motion.span>
                    </motion.button>

                    <AnimatePresence initial={false}>
                        {isOpen && (
                            <motion.div
                                className="nav-submenu__children"
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25, ease: "easeInOut" }}
                            >
                                {item.children.map((child) => (
                                    <motion.button
                                        type="button"
                                        key={child.key}
                                        aria-current={isActive(child.key) ? "page" : undefined}
                                        className={`nav-item nav-item--child ${isActive(child.key) ? "nav-item--active" : ""}`}
                                        onClick={() => handleNavClick(child.key)}
                                        whileHover={{ x: 4 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        <span className="nav-submenu__line" />
                                        <span className="nav-item__label">
                                            <TruncateWithTooltip maxWidth={100}>{child.label}</TruncateWithTooltip>
                                        </span>
                                        {child.betaTag && (
                                            <span className="nav-item__beta">BETA</span>
                                        )}
                                        {child.badge && child.badge > 0 ? (
                                            <Badge count={child.badge} size="small" className="nav-item__badge" />
                                        ) : null}
                                    </motion.button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            );
        }

        // Regular item
        return (
            <motion.button
                type="button"
                key={item.key}
                aria-current={active ? "page" : undefined}
                className={`nav-item ${active ? "nav-item--active" : ""}`}
                variants={itemFadeIn}
                onClick={() => handleNavClick(item.key)}
                whileHover={{ x: 3 }}
                transition={{ duration: 0.15 }}
            >
                <div className="nav-item__icon">
                    {item.icon}
                </div>
                <span className="nav-item__label">
                    <TruncateWithTooltip maxWidth={120}>{item.label}</TruncateWithTooltip>
                </span>
                {item.badge && item.badge > 0 ? (
                    <Badge count={item.badge} size="small" className="nav-item__badge" />
                ) : null}
            </motion.button>
        );
    };

    // ── Sidebar content ──
    const sidebarContent = (
        <motion.div
            className={isSidebarDrawer ? "mobile_side_bar" : "side_bar"}
            initial={isSidebarDrawer ? { x: "-100%" } : false}
            // Desktop width is a CSS custom property (--sidebar-w in
            // sidebar.scss), shared with the content wrapper's offset so the
            // two can never disagree. Framer only drives the drawer slide.
            animate={isSidebarDrawer ? { x: mobileSidebarVisible ? 0 : "-100%" } : {}}
            transition={{
                type: isSidebarDrawer ? "spring" : "tween",
                stiffness: 300,
                damping: 30,
                duration: 0.35,
                ease: [0.4, 0, 0.2, 1],
            }}
            ref={sidebarRef}
        >
            {/* ── Header ── */}
            <div className={`sidebar-header ${collapsed && !isSidebarDrawer ? "sidebar-header--collapsed" : ""}`}>
                <motion.div className="sidebar-header__logo">
                    <motion.img
                        src={logo}
                        alt="P3M"
                        className="sidebar-header__logo-img"
                        animate={{ width: collapsed && !isSidebarDrawer ? 36 : 120 }}
                        transition={{ duration: 0.3 }}
                    />
                </motion.div>

                {!isSidebarDrawer ? (
                    <motion.button
                        className="sidebar-header__toggle"
                        onClick={handleCollapse}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        {collapsed ? (
                            <LayoutSidebarRightIcon size={16} />
                        ) : (
                            // itshover's chevron points right (it's drawn for a right-hand
                            // sidebar); mirrored so it points left, matching this sidebar
                            // collapsing toward the left edge.
                            <span className="sidebar-toggle-icon--mirrored">
                                <LayoutSidebarRightCollapseIcon size={16} />
                            </span>
                        )}
                    </motion.button>
                ) : (
                    <Button
                        type="text"
                        className="sidebar-header__close"
                        icon={<CloseOutlined />}
                        onClick={() => dispatch(closeMobileSidebar())}
                    />
                )}
            </div>

            {/* ── Nav sections ── */}
            <div className="sidebar-nav" role="navigation" aria-label="Primary">
                {shellHydrating ? (
                    <SidebarNavSkeleton
                        collapsed={collapsed && !isSidebarDrawer}
                    />
                ) : (
                <motion.div
                    className="sidebar-nav__inner"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                >
                    {menuSections.map((section, sIdx) => (
                        <div key={section.title} className="nav-section">
                            {(!collapsed || isSidebarDrawer) && (
                                <motion.div
                                    className="nav-section__title"
                                    variants={itemFadeIn}
                                >
                                    {section.title}
                                </motion.div>
                            )}
                            {collapsed && !isSidebarDrawer && sIdx > 0 && (
                                <div className="nav-section__divider" />
                            )}
                            {section.items.map((item, idx) => renderNavItem(item, idx))}
                        </div>
                    ))}
                </motion.div>
                )}
            </div>

            {/* ── Footer / Logout ── */}
            <div className="sidebar-footer">
                {(!collapsed || isSidebarDrawer) ? (
                    <motion.div
                        className="sidebar-footer__logout"
                        onClick={handleLogout}
                        whileHover={{ x: 3 }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <LogoutIcon size={18} />
                        <span className="sidebar-footer__logout-text">Logout</span>
                    </motion.div>
                ) : (
                    <Tooltip title="Logout" placement="right" classNames={{ root: "sidebar-tooltip" }}>
                        <motion.div
                            className="sidebar-footer__logout sidebar-footer__logout--collapsed"
                            onClick={handleLogout}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                        >
                            <LogoutIcon size={18} />
                        </motion.div>
                    </Tooltip>
                )}
            </div>
        </motion.div>
    );

    return (
        /*
         * The org theme is applied here, on the shell that wraps BOTH the
         * sidebar and the navbar, not just on a page's own container.
         *
         * `useAssessmentTheme` publishes the selected organisation's colours
         * as --p3m-* custom properties, and every feature page already spreads
         * them onto its own wrapper. The app chrome sat outside all of those
         * wrappers and hard-coded P3M blue instead, so a branded org got a
         * re-themed page framed by a sidebar and navbar in the wrong brand.
         * Declaring the variables on the shell lets the chrome read the same
         * values the content does; a page that re-declares them on its own
         * wrapper still wins inside itself, and resolves to the same colours
         * regardless, since both read this one hook.
         */
        <div
            className={`layout_container ${theme.cssClass} ${collapsed ? "collapsed" : "expanded"}`}
            style={theme.cssVars}
        >
            {sidebarContent}

            {/* Mobile overlay */}
            <AnimatePresence>
                {isSidebarDrawer && mobileSidebarVisible && (
                    <motion.div
                        className="mobile-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => dispatch(closeMobileSidebar())}
                    />
                )}
            </AnimatePresence>

            {/* Main content. The left offset that clears the fixed sidebar is
                CSS (--sidebar-w), not an inline animation. */}
            <div className="main_content_wrapper">
                <Navbar />
                <ChatbotToggle />
                {children}
            </div>
        </div>
    );
};

export default Sidebar;
